import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBrandConfig, BrandApp } from '../brand-config';
import BrandHeader from '../components/BrandHeader';

function BrandHome() {
  const brandConfig = getBrandConfig();
  const navigate = useNavigate();
  const portalUrl = import.meta.env.VITE_PORTAL_URL || 'https://automations.easystreetbrands.com';

  useEffect(() => {
    document.title = `${brandConfig.name} Automation`;
  }, [brandConfig.name]);

  const shouldOpenInNewTab = (path: string) => {
    try {
      const targetUrl = new URL(path, window.location.origin);
      return targetUrl.origin !== window.location.origin;
    } catch (error) {
      console.warn('Failed to resolve app URL', path, error);
      return true;
    }
  };

  return (
    <div className="app" style={{ '--primary-color': brandConfig.primaryColor } as React.CSSProperties}>
      <BrandHeader />

      <main className="main-content">
        <div className="welcome-section">
          <h2>Welcome to {brandConfig.name}</h2>
          <p>You can access all {brandConfig.name} automation tools from here, or visit the <a href={portalUrl}>main automation portal</a> for the full experience.</p>
          <div className="primary-cta">
            <a href={portalUrl} className="portal-cta-btn">
              Go to Automation Portal
            </a>
          </div>
        </div>
        
        <div className="apps-grid">
          {brandConfig.apps.map((app: BrandApp) => {
            const content = (
              <>
                <div className="app-icon">{app.icon}</div>
                <h3 className="app-name">{app.name}</h3>
                <p className="app-description">{app.description}</p>
                {!app.enabled && <span className="coming-soon">Coming Soon</span>}
              </>
            );

            const className = `app-card ${!app.enabled ? 'disabled' : ''}`;
            const style = { '--app-color': app.color } as React.CSSProperties;

            if (!app.enabled) {
              return (
                <div key={app.id} className={className} style={style}>
                  {content}
                </div>
              );
            }

            if (app.external || app.sameOriginApp) {
              const openInNewTab = shouldOpenInNewTab(app.path);
              return (
                <a
                  key={app.id}
                  className={className}
                  style={style}
                  href={app.path}
                  target={openInNewTab ? '_blank' : undefined}
                  rel={openInNewTab ? 'noopener noreferrer' : undefined}
                >
                  {content}
                </a>
              );
            }

            return (
              <div
                key={app.id}
                className={className}
                style={style}
                onClick={() => navigate(app.path)}
              >
                {content}
              </div>
            );
          })}
        </div>

        <div className="brand-insights">
          <div className="insight-card" onClick={() => navigate('/holy-shipped')}>
            <div className="insight-icon">🚢</div>
            <div className="insight-content">
              <h3>Holy Shipped Weekly Reports</h3>
              <p>Track every feature shipped for {brandConfig.name}, see technical breakdowns, and share stakeholder-ready updates.</p>
            </div>
            <div className="insight-cta">View weekly progress →</div>
          </div>
          <div className="insight-card" onClick={() => navigate('/automation-library')}>
            <div className="insight-icon">⚙️</div>
            <div className="insight-content">
              <h3>Automation Library</h3>
              <p>Browse live automations, understand triggers and actions, and monitor performance for every workflow available.</p>
            </div>
            <div className="insight-cta">Explore automations →</div>
          </div>
        </div>
      </main>

      <footer className="footer">
        <p>&copy; 2024 {brandConfig.name}. Internal automation workspace.</p>
      </footer>
    </div>
  );
}

export default BrandHome;
